// Maps a lens standardType URN to a human-readable dataset Type label.
//
// The label is resolved in four steps:
//   1. Exact "<domain>/<name>" exception (datasets whose Type differs from the
//      title-cased domain, plus custom datasets).
//   2. Per-domain default (for domains whose abbreviation does not title-case
//      into a legible label, e.g. "plce").
//   3. Otherwise the domain segment of the URN, formatted via the caller-supplied
//      titleCase helper (e.g. "purchase-order" -> "Purchase Order").
//   4. Unknown when the URN is missing, unparseable, or not a standard OpenGov URN.
//
// standardType arrives in the lens namespace at runtime
// (urn:opengov:lenses/standard/<domain>/<name>/<version>);
//
// Only the "exception" categories need enum members; domain-derived labels
// (e.g. "Budgeting", "Payments", "Accounting") are produced as strings by the
// title-cased-domain fallback and intentionally have no enum entry.

export enum EDatasetType {
  AccountsPayable = 'Accounts Payable',
  GeneralLedger = 'General Ledger',
  PerformanceManagement = 'Performance Management',
  Workforce = 'Workforce',
  PermittingAndLicensing = 'Permitting & Licensing',
  CustomDataset = 'Custom',
  Unknown = '-'
}

const DATASET_TYPE_KEYS: Partial<Record<EDatasetType, string[]>> = {
  [EDatasetType.AccountsPayable]: [
    'accounting/ap-banks',
    'accounting/ap-banks-psk',
    'accounting/ap-checks',
    'accounting/ap-checks-psk',
    'accounting/ap-inv-wf-inst-levels',
    'accounting/ap-invoice-controls',
    'accounting/ap-invoice-controls-psk',
    'accounting/ap-invoice-details',
    'accounting/ap-invoice-details-psk',
    'accounting/ap-invoice-workflow',
    'accounting/ap-max-effective-date',
    'accounting/ap-payment-check-bank-name',
    'accounting/ap-payment-terms',
    'accounting/ap-tax-code',
    'accounting/ap-tax-payment-types',
    'accounting/ap-vendors'
  ],
  [EDatasetType.GeneralLedger]: [
    'accounting/gl-accounts',
    'accounting/gl-balances',
    'accounting/gl-balances-internal',
    'accounting/gl-details',
    'accounting/gl-details-psk',
    'accounting/gl-fund-transaction-amount',
    'accounting/gl-funds',
    'accounting/gl-funds-psk',
    'accounting/gl-journal-entry-controls',
    'accounting/gl-journal-entry-details',
    'accounting/gl-journal-entry-details-psk',
    'accounting/gl-master',
    'accounting/gl-months',
    'accounting/gl-transactions',
    'accounting/gl-trial-balance'
  ],
  [EDatasetType.PerformanceManagement]: ['budgeting/performance-detailed', 'budgeting/si-approved-proposals'],
  [EDatasetType.Workforce]: [
    'budgeting/wf/detailed',
    'budgeting/wf/fte',
    'budgeting/wf/plan-projection-summary',
    'budgeting/wf/position-costs'
  ],
  [EDatasetType.CustomDataset]: ['opengov/custom', 'opengov/financial-summary', 'opengov/financial-transactions']
};

const DATASET_TYPE_BY_KEY: Record<string, EDatasetType> = Object.entries(DATASET_TYPE_KEYS).reduce(
  (lookup, [type, keys]) => {
    keys?.forEach((key) => {
      lookup[key] = type as EDatasetType;
    });
    return lookup;
  },
  {} as Record<string, EDatasetType>
);

const DATASET_TYPE_BY_DOMAIN: Record<string, EDatasetType> = {
  plce: EDatasetType.PermittingAndLicensing
};

const STANDARD_URN_PREFIX = /^urn:opengov:(?:lenses|datasets)\/standard\//;
const TRAILING_VERSION = /\/\d+$/;

interface IParsedStandardType {
  key: string;
  domain: string;
}

function parseStandardType(standardType: string): IParsedStandardType | null {
  if (!STANDARD_URN_PREFIX.test(standardType)) {
    return null;
  }
  const key = standardType.replace(STANDARD_URN_PREFIX, '').replace(TRAILING_VERSION, '');
  const domain = key.split('/')[0];
  return domain ? { key, domain } : null;
}

type TitleCaseFn = (segment: string | null | undefined) => string;

export function getDatasetTypeLabel(
  standardType: string | null | undefined,
  titleCase: TitleCaseFn
): string {
  try {
    if (typeof standardType !== 'string' || standardType.trim() === '') {
      return EDatasetType.Unknown;
    }
    const parsed = parseStandardType(standardType);
    if (!parsed) {
      return EDatasetType.Unknown;
    }
    const label =
      DATASET_TYPE_BY_KEY[parsed.key] ?? DATASET_TYPE_BY_DOMAIN[parsed.domain] ?? titleCase(parsed.domain);
    return label || EDatasetType.Unknown;
  } catch {
    return EDatasetType.Unknown;
  }
}
